import { useEffect, useImperativeHandle, useRef, forwardRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "lucide-react";

export interface SignaturePadHandle {
  getDataUrl: () => string | null;
  clear: () => void;
  isEmpty: () => boolean;
}

interface SignaturePadProps {
  width?: number;
  height?: number;
  onChange?: (isEmpty: boolean) => void;
}

/**
 * Canvas-based signature pad supporting mouse + touch.
 * Renders an internal high-DPI canvas; getDataUrl() returns a PNG data URL
 * suitable for upload. Empty pad returns null.
 */
export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  ({ width = 600, height = 200, onChange }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const drawingRef = useRef(false);
    const lastPointRef = useRef<{ x: number; y: number } | null>(null);
    const dirtyRef = useRef(false);
    const [, force] = useState(0);

    const setupCanvas = useCallback(() => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas || !wrapper) return;
      const dpr = window.devicePixelRatio || 1;
      const cssWidth = wrapper.clientWidth || width;
      const cssHeight = height;
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      canvas.width = Math.floor(cssWidth * dpr);
      canvas.height = Math.floor(cssHeight * dpr);
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0a0a0a";
      // Repaint a transparent background — actual stroke history is lost on
      // resize, which mirrors common signature pad behavior; users redraw.
      ctx.clearRect(0, 0, cssWidth, cssHeight);
      dirtyRef.current = false;
      onChange?.(true);
    }, [height, width, onChange]);

    useEffect(() => {
      setupCanvas();
      const onResize = () => setupCanvas();
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }, [setupCanvas]);

    const getPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.setPointerCapture(e.pointerId);
      drawingRef.current = true;
      const p = getPoint(e);
      if (!p) return;
      lastPointRef.current = p;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 1.1, 0, Math.PI * 2);
      ctx.fillStyle = "#0a0a0a";
      ctx.fill();
      if (!dirtyRef.current) {
        dirtyRef.current = true;
        onChange?.(false);
        force(n => n + 1);
      }
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current) return;
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      const p = getPoint(e);
      const last = lastPointRef.current;
      if (!p || !last) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      lastPointRef.current = p;
    };

    const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
      drawingRef.current = false;
      lastPointRef.current = null;
      const canvas = canvasRef.current;
      if (canvas && canvas.hasPointerCapture(e.pointerId)) {
        canvas.releasePointerCapture(e.pointerId);
      }
    };

    const clear = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      dirtyRef.current = false;
      onChange?.(true);
      force(n => n + 1);
    };

    useImperativeHandle(ref, () => ({
      getDataUrl: () => {
        const canvas = canvasRef.current;
        if (!canvas || !dirtyRef.current) return null;
        return canvas.toDataURL("image/png");
      },
      clear,
      isEmpty: () => !dirtyRef.current,
    }));

    return (
      <div className="space-y-2">
        <div
          ref={wrapperRef}
          className="relative rounded-md border border-input bg-white"
          data-testid="signature-pad-wrapper"
        >
          <canvas
            ref={canvasRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onPointerLeave={handlePointerUp}
            className="touch-none cursor-crosshair block"
            data-testid="signature-pad-canvas"
          />
          {!dirtyRef.current && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
              Sign here with your mouse or finger
            </div>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            By signing, you agree this electronic signature is the legal
            equivalent of your handwritten signature.
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clear}
            data-testid="button-clear-signature"
          >
            <Eraser className="w-3.5 h-3.5 mr-1.5" />
            Clear
          </Button>
        </div>
      </div>
    );
  }
);

SignaturePad.displayName = "SignaturePad";
